/* =================================================================
   通用 React 组件
   ================================================================= */

const { useState, useEffect, useRef, useMemo, useCallback, Fragment } = React;

/* ---------- 背景沙粒动画 ---------- */

let sandBackgroundCleanup = null;
function startSandBackground() {
  if (sandBackgroundCleanup) sandBackgroundCleanup();
  const canvas = document.getElementById('sand-canvas');
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let frame = 0;
  let stopped = false;
  const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
  canvas.setAttribute('aria-hidden', 'true');

  function resize() {
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    canvas.style.width = window.innerWidth + 'px';
    canvas.style.height = window.innerHeight + 'px';
  }
  resize();
  window.addEventListener('resize', resize);

  const N = Math.min(42, Math.round(window.innerWidth / 35));
  const particles = Array.from({ length: N }).map(() => spawn());

  function spawn() {
    return {
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: (Math.random() * 0.8 + 0.3) * dpr,
      vx: (Math.random() * 0.15 - 0.075) * dpr,
      vy: (Math.random() * 0.18 + 0.04) * dpr,
      a: Math.random() * 0.22 + 0.08,
    };
  }

  function loop() {
    if (stopped) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    particles.forEach(p => {
      p.x += p.vx; p.y += p.vy;
      if (p.y > canvas.height + 4 || p.x < -4 || p.x > canvas.width + 4) {
        Object.assign(p, spawn());
        p.y = -4;
      }
      ctx.fillStyle = `rgba(232, 188, 117, ${p.a})`;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
    });
    if (!motion.matches && !document.hidden) frame = requestAnimationFrame(loop);
  }
  function resume() { cancelAnimationFrame(frame); loop(); }
  document.addEventListener('visibilitychange', resume);
  motion.addEventListener?.('change', resume);
  loop();
  sandBackgroundCleanup = () => {
    stopped = true;
    cancelAnimationFrame(frame);
    window.removeEventListener('resize', resize);
    document.removeEventListener('visibilitychange', resume);
    motion.removeEventListener?.('change', resume);
  };
  return sandBackgroundCleanup;
}

/* ---------- 进度环 ---------- */

function ProgressRing({ percent, size = 56, stroke = 4, color = '#e8bc75', bg = 'rgba(232,188,117,0.12)' }) {
  const value = Math.max(0, Math.min(100, Number(percent) || 0));
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (value / 100) * circ;
  return (
    <svg width={size} height={size} className="progress-ring" role="img" aria-label={`学习进度 ${Math.round(value)}%`}>
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={bg} strokeWidth={stroke} />
      <circle
        cx={size/2} cy={size/2} r={radius}
        fill="none" stroke={color} strokeWidth={stroke}
        strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
      />
    </svg>
  );
}

/* ---------- 模态层 ---------- */

const exhibitionModalStack = [];
let exhibitionModalSequence = 0;
let exhibitionBodyStyle = null;

function Modal({ open, onClose, children, maxW = 'max-w-2xl', allowBackdropClose = true, ariaLabel = '项目操作窗口' }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = `exhibition-modal-${++exhibitionModalSequence}`;

  useEffect(() => {
    if (!open || !dialogRef.current) return;
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    const entry = { id: idRef.current, dialog };
    if (!exhibitionModalStack.length) {
      exhibitionBodyStyle = { overflow: document.body.style.overflow, paddingRight: document.body.style.paddingRight };
      const scrollbar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      const existingPadding = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.overflow = 'hidden';
      if (scrollbar) document.body.style.paddingRight = `${existingPadding + scrollbar}px`;
    }
    exhibitionModalStack.push(entry);
    dialog.parentElement.style.zIndex = String(1000 + exhibitionModalStack.length * 10);
    const isTop = () => exhibitionModalStack[exhibitionModalStack.length - 1] === entry;
    const focusables = () => Array.from(dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), audio[controls], video[controls]'))
      .filter(el => el.tabIndex >= 0 && !el.closest('[inert]') && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden');
    const focusFirst = () => {
      if (!isTop()) return;
      const preferred = dialog.querySelector('[autofocus]');
      const target = preferred || focusables()[0] || dialog;
      target.focus({ preventScroll: true });
    };
    const initialFocus = requestAnimationFrame(focusFirst);
    const onKey = e => {
      if (!isTop()) return;
      if (e.key === 'Escape' && closeRef.current) {
        e.preventDefault();
        e.stopPropagation();
        closeRef.current();
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      const first = items[0], last = items[items.length - 1];
      if (!items.length) { e.preventDefault(); dialog.focus(); return; }
      if (e.shiftKey && (document.activeElement === first || !items.includes(document.activeElement))) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !items.includes(document.activeElement))) {
        e.preventDefault(); first.focus();
      }
    };
    const onFocus = e => { if (isTop() && !dialog.contains(e.target)) focusFirst(); };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('focusin', onFocus);
    return () => {
      cancelAnimationFrame(initialFocus);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('focusin', onFocus);
      const index = exhibitionModalStack.indexOf(entry);
      if (index >= 0) exhibitionModalStack.splice(index, 1);
      if (!exhibitionModalStack.length && exhibitionBodyStyle) {
        document.body.style.overflow = exhibitionBodyStyle.overflow;
        document.body.style.paddingRight = exhibitionBodyStyle.paddingRight;
        exhibitionBodyStyle = null;
      }
      const remaining = exhibitionModalStack[exhibitionModalStack.length - 1];
      if (previousFocus?.isConnected && (!remaining || remaining.dialog.contains(previousFocus))) {
        previousFocus.focus?.({ preventScroll: true });
      } else if (remaining) remaining.dialog.focus({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="modal-backdrop exhibition-modal-backdrop" onClick={e => {
      if (e.target === e.currentTarget && allowBackdropClose && exhibitionModalStack[exhibitionModalStack.length - 1]?.id === idRef.current) onClose?.();
    }}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={ariaLabel} tabIndex={-1}
        className={`glass-card exhibition-dialog w-full ${maxW} max-h-[90vh] overflow-auto p-6 fade-up`} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>, document.body
  );
}

/* ---------- 阶段地层卡 (学生纵向) ---------- */

function StageStratum({ stage, status, onClick, idx }) {
  const cls = ['stage-stratum'];
  if (status === 'completed') cls.push('completed');
  if (status === 'in-progress') cls.push('current');
  if (status === 'locked') cls.push('locked');

  return (
    <button type="button" className={cls.join(' ')} disabled={status === 'locked'}
      aria-label={`阶段 ${idx + 1}，${stage.name}，${status === 'completed' ? '已完成' : status === 'in-progress' ? '进行中' : '待解锁'}`}
      aria-current={status === 'in-progress' ? 'step' : undefined} onClick={() => onClick && onClick()}>
      {status === 'in-progress' && <div className="sand-flow" />}
      <div className="absolute inset-0 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <div className="stage-stratum-mark w-10 h-10 rounded-full flex items-center justify-center text-xl">
            <span aria-hidden="true">{stage.title || stage.icon}</span>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest opacity-70 leading-none">阶段 {idx + 1}</div>
            <div className="font-display text-lg leading-tight">{stage.name}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs opacity-90 font-medium">
            {status === 'completed' ? '✓ 已完成' : status === 'in-progress' ? '进行中' : '待解锁'}
          </div>
          {status !== 'completed' && <div className="text-xs opacity-70">{stage.verb}</div>}
        </div>
      </div>
    </button>
  );
}

/* ---------- 媒体缩略卡 ---------- */

function mediaSource(media) {
  const url = media?.url || media?.data || '';
  return /^(\/media\/|assets\/|\/assets\/|blob:|https?:\/\/|data:(image\/(png|jpeg|gif|webp)|video\/|audio\/|application\/pdf|text\/plain))/.test(url) ? url : '';
}

function MediaPreview({ media, onClose }) {
  const src = mediaSource(media);
  return <Modal open={!!media} onClose={onClose} maxW="max-w-4xl"><div className="media-preview-content"><div className="flex justify-between items-center gap-4 mb-4"><h3>{media?.label || '作品附件'}</h3><button className="sand-button sand-button-ghost" onClick={onClose}>关闭预览</button></div>{!src ? <p>尚未上传原始文件。此条目只有文件说明。</p> : <Fragment>{media.kind === 'image' ? <img src={src} alt={media.label}/> : media.kind === 'video' ? <video src={src} controls playsInline preload="metadata"/> : media.kind === 'audio' ? <audio src={src} controls preload="metadata"/> : <p>文档可通过下方按钮下载查看。</p>}<a className="sand-button sand-button-ghost mt-4" href={src} download={media.label} target="_blank" rel="noopener noreferrer">打开 / 下载原始文件</a></Fragment>}</div></Modal>;
}

function MediaThumb({ media, onPreview, onDelete }) {
  const [preview, setPreview] = useState(false);
  const open = () => onPreview ? onPreview(media) : setPreview(true);
  const source = mediaSource(media);
  const icon = { image: '🖼️', video: '🎬', audio: '🎙️', doc: '📄' }[media.kind] || '📎';
  return (
    <Fragment><div className="media-thumb group" role="button" tabIndex={0}
      aria-label={`预览：${media.label}`}
      onKeyDown={e => { if (e.target === e.currentTarget && ['Enter', ' '].includes(e.key)) { e.preventDefault(); open(); } }}
      onClick={open}>
      {source && media.kind === 'image' ? (
        <img src={source} alt={media.label} loading="lazy" />
      ) : source && media.kind === 'video' ? (
        <video src={source} muted playsInline preload="metadata" />
      ) : (
        <div className="flex flex-col items-center justify-center text-sand-700">
          <div className="text-3xl">{media.emoji || icon}</div>
          <div className="text-xs mt-1">{media.label}</div>
        </div>
      )}
      {onDelete && (
        <button
          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/45 text-white text-xs opacity-0 group-hover:opacity-100 transition"
          onClick={(e) => { e.stopPropagation(); onDelete(media); }}
          title="删除" aria-label={`删除 ${media.label}`} type="button"
        >✕</button>
      )}
      <div className="absolute bottom-0 left-0 right-0 px-2 py-1 text-xs bg-gradient-to-t from-black/40 to-transparent text-white">
        {media.label}
      </div>
    </div>{preview && <MediaPreview media={media} onClose={() => setPreview(false)}/>}</Fragment>
  );
}

/* ---------- 上传区 ---------- */

function UploadZone({ onUpload, hint = '把文件拖到这里，或点击选择', multiple = true }) {
  const inputRef = useRef();
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [progress, setProgress] = useState('');
  const busyRef = useRef(false), mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { if (!busy) return; const warn = e => { e.preventDefault(); e.returnValue = ''; }; window.addEventListener('beforeunload', warn); return () => window.removeEventListener('beforeunload', warn); }, [busy]);

  async function handleFiles(files) {
    if (busyRef.current) return;
    const arr = Array.from(files || []);
    const targetCallback = onUpload, epoch = SandPlatform.generation;
    setError('');
    if (!arr.length) return;
    if (!multiple && arr.length > 1) return setError('这里一次只接收一个附件，请重新选择。');
    if (arr.length > 8) return setError('一次最多选择 8 个文件，请分批上传。');
    const oversized = arr.find(f => f.size > 20 * 1024 * 1024);
    if (oversized) return setError(`${oversized.name} 超过 20 MB，请先压缩或截取短片。`);
    busyRef.current = true; setBusy(true);
    try {
      for (let i = 0; i < arr.length; i++) {
        if (!mounted.current || epoch !== SandPlatform.generation) return;
        setProgress(`正在保存 ${i + 1} / ${arr.length}：${arr[i].name}`);
        const media = await SandPlatform.upload(arr[i]);
        if (!mounted.current || epoch !== SandPlatform.generation) return;
        targetCallback?.(media);
      }
      await SandPlatform.flush(); if (mounted.current) setProgress(`${arr.length} 个附件已保存`);
    } catch (e) { if (mounted.current) setError('上传未全部完成：' + e.message + '。已成功的附件会保留，请仅重试失败项。'); }
    finally { busyRef.current = false; if (mounted.current) setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  }

  return (
    <div
      className={`upload-zone ${drag ? 'dragover' : ''}`}
      role="button" tabIndex={0} aria-label={hint} aria-busy={busy}
      onKeyDown={e => { if (!busy && e.target === e.currentTarget && ['Enter', ' '].includes(e.key)) { e.preventDefault(); inputRef.current?.click(); } }}
      onClick={() => !busy && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
    >
      <div className="upload-symbol mb-3"><ExhibitionIcon name="upload" size={28} /></div>
      <div className="text-sand-800 font-medium">{hint}</div>
      <div className="text-sand-700 text-xs mt-1">支持图片 / 视频 / 音频 / PDF、Office、TXT · 每个不超过 20 MB · 一次最多 8 个</div>
      {progress && <div className="text-xs mt-2" role="status">{progress}{busy ? ' · 完成前请不要切换页面' : ''}</div>}
      {error && <div className="upload-error" role="alert">{error}</div>}
      <input
        type="file"
        ref={inputRef}
        className="hidden"
        multiple={multiple}
        accept=".png,.jpg,.jpeg,.gif,.webp,.mp4,.mov,.webm,.mp3,.wav,.m4a,.ogg,.pdf,.docx,.pptx,.xlsx,.txt,.md,.csv"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}

/* ---------- 评分滑块 ---------- */

function ScoreSlider({ value, onChange, label, desc }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="font-medium text-sand-900">{label}</div>
          <div className="text-xs text-sand-700">{desc}</div>
        </div>
        <div className="text-2xl font-display text-sand-800 tabular-nums">{value}<span className="text-xs opacity-60">/5</span></div>
      </div>
      <input
        type="range"
        min={1} max={5} step={1}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="range-sand"
        aria-label={label} aria-valuetext={`${value} 分，满分 5 分`}
      />
    </div>
  );
}

/* ---------- 轻量 Markdown 渲染 (用于聊天气泡) ---------- */
/* 支持: # ## ### 标题 / **粗** / *斜* / `代码` / - 项 / 1. 序号 / 段落 */

function renderMarkdown(text) {
  if (!text) return null;

  // 行内: **粗** / *斜* / `代码`
  function inline(s, baseKey) {
    const out = [];
    let last = 0;
    const re = /(\*\*([^*\n]+)\*\*|__([^_\n]+)__|`([^`\n]+)`|\*([^*\n]+)\*|_([^_\n]+)_)/g;
    let m;
    while ((m = re.exec(s)) !== null) {
      if (m.index > last) out.push(s.slice(last, m.index));
      const key = baseKey + '-' + m.index;
      if (m[2] || m[3]) out.push(<b key={key}>{m[2] || m[3]}</b>);
      else if (m[4])    out.push(<code key={key} className="px-1 rounded bg-sand-100/80 text-sand-800 text-[0.92em]">{m[4]}</code>);
      else              out.push(<i key={key}>{m[5] || m[6]}</i>);
      last = re.lastIndex;
    }
    if (last < s.length) out.push(s.slice(last));
    return out;
  }

  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let list = null;       // { ordered, items: [string] }
  let para = [];

  function flushList() {
    if (!list) return;
    const Tag = list.ordered ? 'ol' : 'ul';
    blocks.push(
      <Tag key={'l' + blocks.length} className={`my-1.5 pl-5 space-y-0.5 ${list.ordered ? 'list-decimal' : 'list-disc'}`}>
        {list.items.map((it, i) => <li key={i}>{inline(it, 'li' + blocks.length + i)}</li>)}
      </Tag>
    );
    list = null;
  }
  function flushPara() {
    if (para.length === 0) return;
    blocks.push(
      <p key={'p' + blocks.length} className="my-1 leading-relaxed whitespace-pre-wrap">
        {inline(para.join('\n'), 'p' + blocks.length)}
      </p>
    );
    para = [];
  }
  function flushAll() { flushList(); flushPara(); }

  for (const raw of lines) {
    // 标题
    const h = raw.match(/^\s*(#{1,4})\s+(.+?)\s*#*\s*$/);
    if (h) {
      flushAll();
      const lv = h[1].length;
      const cls = lv === 1 ? 'text-xl font-display mt-2 mb-1'
                 : lv === 2 ? 'text-lg font-display mt-2 mb-1'
                 : lv === 3 ? 'text-base font-medium mt-2 mb-0.5'
                 : 'text-sm font-medium mt-1.5 mb-0.5';
      blocks.push(<div key={'h' + blocks.length} className={`${cls} text-sand-900`}>{inline(h[2], 'h' + blocks.length)}</div>);
      continue;
    }
    // 引用
    const q = raw.match(/^\s*>\s+(.+)$/);
    if (q) {
      flushAll();
      blocks.push(<div key={'q' + blocks.length} className="my-1 pl-3 border-l-2 border-sand-400 text-sand-800 italic">{inline(q[1], 'q' + blocks.length)}</div>);
      continue;
    }
    // 无序列表
    const ul = raw.match(/^\s*[-*+]\s+(.+)$/);
    if (ul) {
      flushPara();
      if (list && list.ordered) flushList();
      if (!list) list = { ordered: false, items: [] };
      list.items.push(ul[1]);
      continue;
    }
    // 有序列表
    const ol = raw.match(/^\s*\d+[.)]\s+(.+)$/);
    if (ol) {
      flushPara();
      if (list && !list.ordered) flushList();
      if (!list) list = { ordered: true, items: [] };
      list.items.push(ol[1]);
      continue;
    }
    // 分隔线
    if (/^\s*[-*_]{3,}\s*$/.test(raw)) {
      flushAll();
      blocks.push(<hr key={'hr' + blocks.length} className="my-2 border-sand-300/50" />);
      continue;
    }
    // 空行 → 段落结束
    if (raw.trim() === '') { flushAll(); continue; }
    // 普通行 → 累积到当前段落
    flushList();
    para.push(raw);
  }
  flushAll();
  return blocks;
}

/* ---------- 9 个智能体的 SVG 角色形象 ---------- */
/* 用矢量图形绘制每个角色的"工装+道具+场景"画像                 */

function RoleCharacter({ role, size = 180, animate = true }) {
  const source = `assets/exhibition/roles/${role.id}.jpg`;
  const [failedSource, setFailedSource] = useState('');
  const [loadedSource, setLoadedSource] = useState('');
  const failed = failedSource === source;
  const loaded = loadedSource === source && !failed;
  return (
    <div className={`role-portrait ${animate ? 'role-portrait-motion' : ''} ${loaded ? 'is-loaded' : ''}`}
      style={{ width: size, height: size * 1.2, '--role-accent': role.color || '#e8bc75' }}>
      {!loaded && <div className="role-portrait-fallback"><RoleCharacterFallback role={role} size={size} animate={false} /></div>}
      {!failed && <img className="role-portrait-image" src={source} alt={`${role.roleName || role.fullName || '项目智能体'}角色形象`}
        width={size} height={Math.round(size * 1.2)} decoding="async"
        onLoad={() => setLoadedSource(source)} onError={() => setFailedSource(source)} />}
      <span className="role-portrait-edge" aria-hidden="true" />
    </div>
  );
}

let exhibitionSvgSequence = 0;
function RoleCharacterFallback({ role, size = 180, animate = false }) {
  const id = useRef(null);
  if (id.current === null) id.current = ++exhibitionSvgSequence;
  const c = CHARACTERS[role.id];
  if (!c) return null;
  const gradId = `bg-grad-${role.id}-${id.current}`;
  const skinId = `skin-${role.id}-${id.current}`;
  return (
    <svg viewBox="0 0 200 240" width={size} height={(size * 240) / 200} className={animate ? 'role-character' : ''}
      role="img" aria-label={`${role.roleName || role.fullName || '项目智能体'}角色形象`}>
      <defs>
        <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"  stopColor={c.bgGrad[0]} />
          <stop offset="100%" stopColor={c.bgGrad[1]} />
        </linearGradient>
        <radialGradient id={skinId} cx="0.4" cy="0.35" r="0.7">
          <stop offset="0%" stopColor="#ffd6a5" />
          <stop offset="100%" stopColor={c.skin} />
        </radialGradient>
      </defs>

      {/* 背景圆 + 场景剪影 */}
      <circle cx="100" cy="120" r="118" fill={`url(#${gradId})`} />
      <SceneShape kind={c.sceneShape} />

      {/* 身体/工装 */}
      <BodyShape c={c} />

      {/* 道具 (放在身体边/手) */}
      <PropShape kind={c.prop} c={c} />

      {/* 脖子 */}
      <rect x="88" y="125" width="24" height="18" fill={`url(#${skinId})`} />

      {/* 头 + 头发 + 面部 + 帽子 */}
      <HeadShape c={c} skinFill={`url(#${skinId})`} />
    </svg>
  );
}

/* ----- 场景剪影 ----- */
function SceneShape({ kind }) {
  switch (kind) {
    case 'mountains':
      return (
        <g opacity="0.45">
          <path d="M-10 175 L40 110 L80 145 L120 95 L160 140 L210 100 L210 240 L-10 240 Z" fill="#3d2715" opacity="0.22"/>
          <path d="M-10 200 L60 150 L130 185 L210 145 L210 240 L-10 240 Z" fill="#3d2715" opacity="0.32"/>
        </g>
      );
    case 'track':
      return (
        <g opacity="0.4">
          <path d="M-10 195 Q100 175 210 195 L210 240 L-10 240 Z" fill="#5b1a0c" opacity="0.3"/>
          <circle cx="40" cy="60" r="14" fill="#fff5dc" opacity="0.3"/>
        </g>
      );
    case 'skyline':
      return (
        <g opacity="0.5">
          <rect x="20" y="155" width="22" height="60" fill="#5b3a1f" opacity="0.4"/>
          <rect x="48" y="135" width="18" height="80" fill="#5b3a1f" opacity="0.45"/>
          <rect x="72" y="160" width="22" height="55" fill="#5b3a1f" opacity="0.4"/>
          <rect x="118" y="140" width="20" height="75" fill="#5b3a1f" opacity="0.45"/>
          <rect x="144" y="155" width="24" height="60" fill="#5b3a1f" opacity="0.4"/>
          <circle cx="160" cy="55" r="14" fill="#f6d57a" opacity="0.7"/>
        </g>
      );
    case 'tower':
      return (
        <g opacity="0.5">
          <path d="M85 90 L115 90 L120 230 L80 230 Z" fill="#3d2715" opacity="0.32"/>
          <rect x="78" y="120" width="44" height="3" fill="#3d2715" opacity="0.35"/>
          <rect x="78" y="150" width="44" height="3" fill="#3d2715" opacity="0.35"/>
          <rect x="78" y="180" width="44" height="3" fill="#3d2715" opacity="0.35"/>
          <path d="M88 90 L100 70 L112 90 Z" fill="#3d2715" opacity="0.35"/>
        </g>
      );
    case 'oasis':
      return (
        <g opacity="0.55">
          <ellipse cx="35" cy="200" rx="40" ry="14" fill="#356f49" opacity="0.35"/>
          <ellipse cx="170" cy="195" rx="32" ry="12" fill="#356f49" opacity="0.4"/>
          <path d="M30 200 Q33 175 35 200" stroke="#356f49" strokeWidth="2" fill="none" opacity="0.6"/>
          <path d="M40 200 Q43 178 45 200" stroke="#356f49" strokeWidth="2" fill="none" opacity="0.6"/>
          <circle cx="170" cy="60" r="12" fill="#fff5dc" opacity="0.6"/>
        </g>
      );
    case 'sound-waves':
      return (
        <g opacity="0.5" stroke="#5b3a1f" strokeWidth="2" fill="none">
          <path d="M10 60 Q40 50 70 60 T130 60 T190 60" opacity="0.35"/>
          <path d="M10 80 Q40 65 70 80 T130 80 T190 80" opacity="0.3"/>
          <path d="M10 50 Q40 40 70 50 T130 50 T190 50" opacity="0.25"/>
        </g>
      );
    case 'cactus':
      return (
        <g opacity="0.55">
          <path d="M30 230 L30 175 Q30 165 38 165 L42 165 Q50 165 50 175 L50 230 Z" fill="#356f49" opacity="0.5"/>
          <path d="M38 200 L38 185 L28 185" stroke="#356f49" strokeWidth="3" fill="none" opacity="0.5"/>
          <path d="M170 230 L170 190 Q170 180 178 180 L178 230 Z" fill="#356f49" opacity="0.4"/>
          <circle cx="160" cy="65" r="10" fill="#f6d57a" opacity="0.6"/>
        </g>
      );
    case 'gears':
      return (
        <g opacity="0.5" stroke="#5b3a1f" strokeWidth="2" fill="none">
          <circle cx="35" cy="60" r="15" opacity="0.4"/>
          <circle cx="35" cy="60" r="6" opacity="0.4"/>
          <circle cx="165" cy="50" r="22" opacity="0.35"/>
          <circle cx="165" cy="50" r="8" opacity="0.35"/>
          <line x1="40" y1="200" x2="160" y2="200" opacity="0.3"/>
          <line x1="40" y1="200" x2="40" y2="220" opacity="0.3"/>
          <line x1="160" y1="200" x2="160" y2="220" opacity="0.3"/>
          <line x1="100" y1="200" x2="100" y2="215" opacity="0.3"/>
        </g>
      );
    case 'art-arch':
      return (
        <g opacity="0.5">
          <path d="M40 230 L40 130 Q100 90 160 130 L160 230" stroke="#a85a32" strokeWidth="4" fill="none" opacity="0.45"/>
          <circle cx="60" cy="200" r="5" fill="#fff5dc" opacity="0.6"/>
          <circle cx="140" cy="200" r="5" fill="#fff5dc" opacity="0.6"/>
          <circle cx="100" cy="160" r="6" fill="#fff5dc" opacity="0.6"/>
        </g>
      );
    default: return null;
  }
}

/* ----- 身体/工装 ----- */
function BodyShape({ c }) {
  return (
    <g>
      {/* 肩膀+躯干 */}
      <path d="M40 240 L40 200 Q40 175 70 165 L130 165 Q160 175 160 200 L160 240 Z"
            fill={c.outfit.color} />
      {/* 翻领 */}
      <path d="M85 165 L100 195 L115 165 L100 175 Z" fill={c.outfit.collar} />
      {/* 装饰条 */}
      <path d="M40 195 L160 195" stroke="rgba(255,255,255,0.18)" strokeWidth="2"/>
      {/* 领带 (仅 invest) */}
      {c.outfit.tie && (
        <path d="M95 175 L100 195 L105 175 L102 168 L98 168 Z" fill={c.outfit.tie}/>
      )}
    </g>
  );
}

/* ----- 头 + 头发 + 面部 + 帽子 ----- */
function HeadShape({ c, skinFill }) {
  return (
    <g>
      {/* 头发底层 (耳后/脖子的头发) */}
      <HairBack c={c} />
      {/* 脸 */}
      <ellipse cx="100" cy="100" rx="38" ry="42" fill={skinFill} />
      {/* 头发顶层 */}
      <HairFront c={c} />
      {/* 帽子 (覆盖头发) */}
      <HatShape c={c} />
      {/* 眼睛 */}
      <g fill={c.accent}>
        <circle cx="86" cy="103" r="2.5" />
        <circle cx="114" cy="103" r="2.5" />
        <ellipse cx="86" cy="102.3" rx="0.8" ry="0.6" fill="#fff" />
        <ellipse cx="114" cy="102.3" rx="0.8" ry="0.6" fill="#fff" />
      </g>
      {/* 腮红 */}
      <g opacity="0.4">
        <circle cx="78" cy="112" r="5" fill="#d56455"/>
        <circle cx="122" cy="112" r="5" fill="#d56455"/>
      </g>
      {/* 嘴 (浅笑) */}
      <path d="M92 118 Q100 124 108 118" stroke={c.accent} strokeWidth="1.8" strokeLinecap="round" fill="none"/>
    </g>
  );
}

function HairBack({ c }) {
  const color = c.hairColor;
  switch (c.hairStyle) {
    case 'long':
      return <path d="M62 100 Q60 165 80 175 L120 175 Q140 165 138 100 Q138 70 100 65 Q62 70 62 100 Z" fill={color}/>;
    case 'wavy':
      return <path d="M64 95 Q66 145 84 158 L116 158 Q134 145 136 95 Q132 72 100 68 Q68 72 64 95 Z" fill={color}/>;
    case 'bun':
      return (
        <g fill={color}>
          <circle cx="100" cy="60" r="14" />
          <path d="M68 100 Q66 130 80 140 L120 140 Q134 130 132 100 Z" />
        </g>
      );
    default: return null;
  }
}

function HairFront({ c }) {
  const color = c.hairColor;
  switch (c.hairStyle) {
    case 'short-cap':   // 短发,前刘海
      return <path d="M65 88 Q75 70 100 65 Q125 70 135 88 Q120 80 100 80 Q80 80 65 88 Z" fill={color}/>;
    case 'short':
      return <path d="M62 90 Q70 65 100 62 Q130 65 138 90 Q120 76 100 76 Q80 76 62 90 Z" fill={color}/>;
    case 'spiky':
      return <path d="M65 85 L72 65 L80 82 L88 60 L96 80 L104 60 L112 82 L120 65 L128 82 L135 70 L138 92 Q120 80 100 80 Q80 80 65 85 Z" fill={color}/>;
    case 'long':
      return <path d="M66 88 Q72 68 100 65 Q128 68 134 88 Q120 78 100 78 Q80 78 66 88 Z" fill={color}/>;
    case 'wavy':
      return <path d="M66 90 Q78 70 95 68 Q120 65 134 90 Q120 85 105 80 Q90 78 80 88 Q72 92 66 90 Z" fill={color}/>;
    case 'bun':
      return <path d="M66 88 Q74 70 100 68 Q126 70 134 88 Q120 80 100 80 Q80 80 66 88 Z" fill={color}/>;
    default: return null;
  }
}

function HatShape({ c }) {
  switch (c.hat.type) {
    case 'hard-hat':
      return (
        <g>
          <path d="M58 80 Q100 36 142 80 L142 84 L58 84 Z" fill={c.hat.color}/>
          <rect x="56" y="84" width="88" height="6" rx="2" fill={c.hat.color} opacity="0.8"/>
          <rect x="96" y="48" width="8" height="8" fill="rgba(255,255,255,0.3)" rx="1"/>
        </g>
      );
    case 'headband':
      return <rect x="58" y="76" width="84" height="10" rx="2" fill={c.hat.color}/>;
    case 'sun-hat':
      return (
        <g>
          <ellipse cx="100" cy="80" rx="58" ry="14" fill={c.hat.color}/>
          <ellipse cx="100" cy="72" rx="36" ry="20" fill={c.hat.color}/>
          <path d="M64 80 Q100 86 136 80" stroke="#7e4f23" strokeWidth="2" fill="none" opacity="0.4"/>
        </g>
      );
    case 'headphones':
      return (
        <g>
          <path d="M60 85 Q60 55 100 55 Q140 55 140 85" stroke={c.hat.color} strokeWidth="6" fill="none"/>
          <rect x="50" y="84" width="20" height="26" rx="6" fill={c.hat.color}/>
          <rect x="130" y="84" width="20" height="26" rx="6" fill={c.hat.color}/>
          <rect x="54" y="88" width="12" height="18" rx="3" fill="#3d2715" opacity="0.5"/>
          <rect x="134" y="88" width="12" height="18" rx="3" fill="#3d2715" opacity="0.5"/>
        </g>
      );
    case 'explorer-hat':
      return (
        <g>
          <ellipse cx="100" cy="80" rx="54" ry="12" fill={c.hat.color}/>
          <path d="M70 75 Q70 50 100 48 Q130 50 130 75 Z" fill={c.hat.color}/>
          <rect x="70" y="73" width="60" height="6" fill="#3d2715" opacity="0.35"/>
          <rect x="118" y="64" width="10" height="14" fill="#fff5dc" opacity="0.6"/>
        </g>
      );
    case 'monocle':
      return (
        <g>
          <circle cx="114" cy="103" r="7" stroke={c.hat.color} strokeWidth="1.6" fill="rgba(255,255,255,0.18)"/>
          <line x1="120" y1="108" x2="125" y2="120" stroke={c.hat.color} strokeWidth="1.2"/>
        </g>
      );
    case 'beret':
      return (
        <g>
          <ellipse cx="100" cy="68" rx="32" ry="10" fill={c.hat.color}/>
          <ellipse cx="100" cy="64" rx="28" ry="12" fill={c.hat.color}/>
          <circle cx="124" cy="60" r="3.5" fill={c.hat.color}/>
        </g>
      );
    case 'glasses':
      return (
        <g stroke={c.hat.color} strokeWidth="2" fill="none">
          <rect x="76" y="96" width="18" height="14" rx="3" fill="rgba(255,255,255,0.25)"/>
          <rect x="106" y="96" width="18" height="14" rx="3" fill="rgba(255,255,255,0.25)"/>
          <line x1="94" y1="103" x2="106" y2="103"/>
        </g>
      );
    default: return null;
  }
}

/* ----- 道具 (放在身体右侧或手中) ----- */
function PropShape({ kind, c }) {
  switch (kind) {
    case 'shovel':
      return (
        <g transform="translate(150,170) rotate(20)">
          <rect x="-2" y="-32" width="4" height="48" fill="#7e4f23"/>
          <path d="M-9 18 L9 18 L9 30 Q0 38 -9 30 Z" fill="#bd8136"/>
        </g>
      );
    case 'beanbag':
      return (
        <g transform="translate(150,175)">
          <ellipse cx="0" cy="0" rx="14" ry="11" fill="#a85a32"/>
          <path d="M-12 -3 Q0 -8 12 -3" stroke="#fff5dc" strokeWidth="1.2" fill="none"/>
          <path d="M-12 3 Q0 8 12 3" stroke="#fff5dc" strokeWidth="1.2" fill="none"/>
        </g>
      );
    case 'briefcase':
      return (
        <g transform="translate(150,180)">
          <rect x="-14" y="-10" width="28" height="22" rx="2" fill="#5b3a1f"/>
          <rect x="-12" y="-8" width="24" height="18" fill="#b8852c" opacity="0.6"/>
          <rect x="-3" y="-15" width="6" height="5" rx="1" fill="#5b3a1f"/>
        </g>
      );
    case 'rammer':
      return (
        <g transform="translate(155,178) rotate(15)">
          <rect x="-2" y="-30" width="4" height="46" fill="#7e4f23"/>
          <rect x="-9" y="16" width="18" height="14" rx="2" fill="#5b3a1f"/>
        </g>
      );
    case 'sapling':
      return (
        <g transform="translate(150,180)">
          <rect x="-8" y="0" width="16" height="14" rx="2" fill="#7e4f23"/>
          <rect x="-1" y="-12" width="2" height="14" fill="#356f49"/>
          <ellipse cx="-5" cy="-12" rx="6" ry="4" fill="#6ea674"/>
          <ellipse cx="5" cy="-15" rx="5" ry="4" fill="#6ea674"/>
          <ellipse cx="0" cy="-18" rx="6" ry="4" fill="#4f8a5b"/>
        </g>
      );
    case 'mic':
      return (
        <g transform="translate(152,175) rotate(15)">
          <rect x="-2" y="-2" width="4" height="22" fill="#3d2715"/>
          <ellipse cx="0" cy="-10" rx="9" ry="11" fill="#5b3a1f"/>
          <path d="M-9 -10 L9 -10 M-9 -14 L9 -14 M-9 -6 L9 -6" stroke="#fff5dc" strokeWidth="0.8"/>
        </g>
      );
    case 'magnifier':
      return (
        <g transform="translate(155,175) rotate(20)">
          <circle cx="0" cy="0" r="13" stroke="#5b3a1f" strokeWidth="3" fill="rgba(255,255,255,0.25)"/>
          <line x1="9" y1="9" x2="20" y2="20" stroke="#5b3a1f" strokeWidth="4" strokeLinecap="round"/>
        </g>
      );
    case 'hourglass':
      return (
        <g transform="translate(150,175)">
          <rect x="-10" y="-13" width="20" height="2" fill="#5b3a1f"/>
          <rect x="-10" y="13" width="20" height="2" fill="#5b3a1f"/>
          <path d="M-9 -11 L9 -11 L0 0 L9 11 L-9 11 L0 0 Z" fill="#e9c98b" stroke="#5b3a1f" strokeWidth="1.5"/>
          <path d="M-9 -11 L0 0 L9 -11 Z" fill="#bd8136" opacity="0.7"/>
        </g>
      );
    case 'brush':
      return (
        <g transform="translate(152,175) rotate(35)">
          <rect x="-2" y="-22" width="4" height="34" fill="#7e4f23"/>
          <rect x="-4" y="12" width="8" height="6" fill="#a85a32"/>
          <path d="M-5 18 L5 18 L4 28 L-4 28 Z" fill="#d49f3a"/>
        </g>
      );
    default: return null;
  }
}

/* ---------- 八大武器 SVG 图标 ---------- */

function WeaponIcon({ weapon, size = 72, equipped = false }) {
  const id = useRef(null);
  if (id.current === null) id.current = ++exhibitionSvgSequence;
  const backgroundId = `wg-${weapon.id}-${id.current}`;
  const metalId = `metal-${weapon.id}-${id.current}`;

  return (
    <svg viewBox="0 0 100 100" width={size} height={size}
         role="img" aria-label={`${weapon.name || 'AI 学习工具'}${equipped ? '，已装备' : ''}`}
         className={equipped ? 'weapon-icon equipped' : 'weapon-icon'}>
      <defs>
        <radialGradient id={backgroundId} cx="0.5" cy="0.3" r="0.8">
          <stop offset="0%" stopColor="#33434a" />
          <stop offset="100%" stopColor="#19262e" />
        </radialGradient>
        <linearGradient id={metalId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#f4eedf" />
          <stop offset="100%" stopColor="#9c8762" />
        </linearGradient>
      </defs>
      {/* 圆形背景 */}
      <circle cx="50" cy="50" r="48" fill={`url(#${backgroundId})`} />
      <circle cx="50" cy="50" r="48" fill="none" stroke={equipped ? '#8bcbc2' : '#e8bc75'} strokeOpacity={equipped ? 0.9 : 0.24} strokeWidth="1" />

      {weapon.icon === 'saw'         && <SawIcon />}
      {weapon.icon === 'scan-pen'    && <ScanPenIcon metalId={metalId} />}
      {weapon.icon === 'shovel'      && <ShovelIcon metalId={metalId} />}
      {weapon.icon === 'spotlight'   && <SpotlightIcon />}
      {weapon.icon === 'sticky-glow' && <StickyGlowIcon />}
      {weapon.icon === 'safety-net'  && <SafetyNetIcon />}
      {weapon.icon === 'shapeshifter'&& <ShapeshifterIcon />}
      {weapon.icon === 'compass'     && <CompassIcon />}

      {/* 装备时的高光环 */}
      {equipped && (
        <circle cx="50" cy="50" r="43" fill="none" stroke="#8bcbc2" strokeWidth="1" strokeDasharray="2 8" opacity="0.7" />
      )}
    </svg>
  );
}

function lighten(hex, amt) {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.min(255, ((n >> 16) & 0xff) + Math.round(255 * amt));
  const g = Math.min(255, ((n >> 8)  & 0xff) + Math.round(255 * amt));
  const b = Math.min(255, ( n        & 0xff) + Math.round(255 * amt));
  return '#' + [r,g,b].map(v => v.toString(16).padStart(2,'0')).join('');
}

/* 1. 零件分离机 — 锯片 + 手柄 */
function SawIcon() {
  return (
    <g>
      {/* 锯刀片 */}
      <g transform="translate(50 48)">
        <circle r="22" fill="#e9e0c8" stroke="#3d2715" strokeWidth="1.5"/>
        <circle r="6" fill="#7e4f23"/>
        <circle r="2.5" fill="#fff5dc"/>
        {/* 锯齿 */}
        {Array.from({length:14}).map((_,i)=>{
          const a = (i*360/14) * Math.PI/180;
          const x1 = Math.cos(a)*22, y1 = Math.sin(a)*22;
          const x2 = Math.cos(a + Math.PI/28)*28, y2 = Math.sin(a + Math.PI/28)*28;
          const x3 = Math.cos(a + Math.PI/14)*22, y3 = Math.sin(a + Math.PI/14)*22;
          return <polygon key={i} points={`${x1},${y1} ${x2},${y2} ${x3},${y3}`} fill="#bd8136"/>;
        })}
      </g>
      {/* 手柄 */}
      <rect x="30" y="78" width="40" height="8" rx="3" fill="#5b3a1f"/>
      <rect x="34" y="80" width="32" height="4" fill="#d49f3a"/>
      {/* 火花 */}
      <circle cx="76" cy="32" r="2" fill="#fff5dc" opacity="0.8"/>
      <circle cx="82" cy="40" r="1.5" fill="#fff5dc" opacity="0.6"/>
    </g>
  );
}

/* 2. 全息扫描笔 — 笔身 + 光束 */
function ScanPenIcon({ metalId }) {
  return (
    <g>
      {/* 光束 */}
      <g opacity="0.7">
        <path d="M 30 75 L 18 88 L 24 88 L 30 78 Z" fill="#fff5dc" opacity="0.85"/>
        <path d="M 30 75 Q 22 82 16 88" stroke="#fff5dc" strokeWidth="1.2" fill="none" opacity="0.6"/>
      </g>
      {/* 笔身 (倾斜) */}
      <g transform="rotate(-35 50 50)">
        <rect x="38" y="20" width="24" height="50" rx="4" fill={`url(#${metalId})`}/>
        <rect x="38" y="20" width="24" height="8" rx="4" fill="#5b3a1f"/>
        <rect x="40" y="30" width="20" height="4" fill="#d49f3a"/>
        <rect x="40" y="40" width="20" height="2" fill="#d49f3a" opacity="0.7"/>
        {/* 笔尖 */}
        <polygon points="50,70 44,80 56,80" fill="#3d2715"/>
        <polygon points="50,72 47,77 53,77" fill="#ffcd6a"/>
      </g>
      {/* 全息粒子 */}
      <circle cx="22" cy="78" r="1.5" fill="#fff5dc" opacity="0.85"/>
      <circle cx="18" cy="72" r="1" fill="#fff5dc" opacity="0.6"/>
      <circle cx="14" cy="82" r="1.2" fill="#fff5dc" opacity="0.7"/>
    </g>
  );
}

/* 3. 深度寻宝铲 — 铲头入土 + 闪光宝藏 */
function ShovelIcon({ metalId }) {
  return (
    <g>
      {/* 土堆背景 */}
      <ellipse cx="50" cy="75" rx="38" ry="14" fill="#5b3a1f" opacity="0.5"/>
      {/* 宝藏闪光 */}
      <g transform="translate(65 72)">
        <circle r="6" fill="#ffcd6a"/>
        <polygon points="0,-9 2,-3 9,0 2,3 0,9 -2,3 -9,0 -2,-3" fill="#fff5dc" opacity="0.85"/>
      </g>
      {/* 铲子 (倾斜) */}
      <g transform="rotate(20 50 50)">
        {/* 杆 */}
        <rect x="46" y="10" width="8" height="50" fill="#7e4f23"/>
        <rect x="46" y="10" width="8" height="50" fill={`url(#${metalId})`} opacity="0.4"/>
        {/* 把手 */}
        <rect x="38" y="8" width="24" height="6" rx="2" fill="#5b3a1f"/>
        {/* 铲头 */}
        <path d="M 38 60 L 62 60 L 56 80 Q 50 86 44 80 Z" fill="#d4d4d8" stroke="#3d2715" strokeWidth="1.5"/>
        <path d="M 42 64 L 58 64" stroke="#3d2715" strokeWidth="0.5" opacity="0.5"/>
      </g>
    </g>
  );
}

/* 4. 灵感探照灯 — 灯泡 + 多束光 */
function SpotlightIcon() {
  return (
    <g>
      {/* 多束光 */}
      <g opacity="0.55">
        <polygon points="50,40 20,12 28,8" fill="#fff5dc"/>
        <polygon points="50,40 36,8 44,6" fill="#fff5dc"/>
        <polygon points="50,40 50,6 58,6" fill="#fff5dc"/>
        <polygon points="50,40 72,8 64,4" fill="#fff5dc"/>
        <polygon points="50,40 86,16 80,8" fill="#fff5dc"/>
      </g>
      {/* 灯罩 */}
      <path d="M 36 38 L 64 38 L 60 56 L 40 56 Z" fill="#4f8a5b" stroke="#1f3e26" strokeWidth="1.5"/>
      <ellipse cx="50" cy="38" rx="14" ry="3" fill="#356f49"/>
      {/* 灯泡 */}
      <circle cx="50" cy="48" r="6" fill="#ffd98a"/>
      <circle cx="48" cy="46" r="2" fill="#fff5dc"/>
      {/* 灯柱 + 底座 */}
      <rect x="48" y="56" width="4" height="22" fill="#3d2715"/>
      <ellipse cx="50" cy="80" rx="14" ry="4" fill="#3d2715"/>
      <ellipse cx="50" cy="78" rx="14" ry="3" fill="#5b3a1f"/>
    </g>
  );
}

/* 5. 发光记忆贴 — 便签 + 光晕 */
function StickyGlowIcon() {
  return (
    <g>
      {/* 光晕 */}
      <circle cx="50" cy="50" r="40" fill="#fff5dc" opacity="0.25"/>
      <circle cx="50" cy="50" r="30" fill="#ffd98a" opacity="0.2"/>
      {/* 便签卡 1 (后) */}
      <g transform="rotate(-8 50 50)">
        <rect x="22" y="26" width="56" height="48" rx="3" fill="#fff5dc" stroke="#3d2715" strokeWidth="1"/>
        <line x1="28" y1="40" x2="72" y2="40" stroke="#3d2715" strokeWidth="0.8" opacity="0.4"/>
        <line x1="28" y1="50" x2="64" y2="50" stroke="#3d2715" strokeWidth="0.8" opacity="0.4"/>
        <line x1="28" y1="60" x2="70" y2="60" stroke="#3d2715" strokeWidth="0.8" opacity="0.4"/>
      </g>
      {/* 便签卡 2 (前,主) */}
      <g transform="rotate(6 50 50)">
        <rect x="26" y="30" width="52" height="44" rx="3" fill="#ffd98a" stroke="#3d2715" strokeWidth="1.2"/>
        <text x="52" y="58" fontSize="14" fontFamily="serif" fontWeight="bold" textAnchor="middle" fill="#5b3a1f">线索</text>
      </g>
      {/* 闪光 */}
      <circle cx="80" cy="28" r="2" fill="#fff" opacity="0.9"/>
      <circle cx="86" cy="36" r="1.5" fill="#fff" opacity="0.7"/>
    </g>
  );
}

/* 6. 记忆防坠网 — 网格 + 落下的物品 */
function SafetyNetIcon() {
  return (
    <g>
      {/* 落下的"知识"块 (在网上方) */}
      <rect x="42" y="14" width="16" height="14" rx="2" fill="#ffd98a" stroke="#3d2715" strokeWidth="1"/>
      <text x="50" y="25" fontSize="9" fontFamily="serif" textAnchor="middle" fill="#5b3a1f">A</text>
      {/* 网架 */}
      <path d="M 12 40 Q 12 70 50 78 Q 88 70 88 40" stroke="#5b3a1f" strokeWidth="2" fill="none"/>
      <line x1="12" y1="40" x2="88" y2="40" stroke="#5b3a1f" strokeWidth="1.5"/>
      {/* 网格 */}
      <g stroke="#5b3a1f" strokeWidth="0.9" fill="none" opacity="0.7">
        {/* 横向 */}
        <path d="M 15 45 Q 50 60 85 45"/>
        <path d="M 18 55 Q 50 70 82 55"/>
        <path d="M 22 65 Q 50 78 78 65"/>
        {/* 纵向 */}
        <path d="M 30 41 Q 30 67 33 76"/>
        <path d="M 42 41 Q 42 70 44 78"/>
        <path d="M 58 41 Q 58 70 56 78"/>
        <path d="M 70 41 Q 70 67 67 76"/>
      </g>
      {/* 网柱 */}
      <rect x="10" y="38" width="4" height="40" fill="#3d2715"/>
      <rect x="86" y="38" width="4" height="40" fill="#3d2715"/>
    </g>
  );
}

/* 7. 百变测试仪 — 多面立方 / 变形齿轮 */
function ShapeshifterIcon() {
  return (
    <g>
      {/* 中心圆 */}
      <circle cx="50" cy="50" r="20" fill="#a3672a" stroke="#3d2715" strokeWidth="1.5"/>
      {/* 4 个形状变体围绕 */}
      <g>
        {/* 圆 */}
        <circle cx="50" cy="20" r="8" fill="#fff5dc" stroke="#3d2715" strokeWidth="1"/>
        {/* 方 */}
        <rect x="72" y="42" width="16" height="16" fill="#ffd98a" stroke="#3d2715" strokeWidth="1"/>
        {/* 三角 */}
        <polygon points="50,72 42,86 58,86" fill="#c9a875" stroke="#3d2715" strokeWidth="1"/>
        {/* 星 */}
        <polygon points="20,50 14,46 16,42 12,38 20,38 22,32 26,38 30,38 28,44 26,50 22,46" fill="#d49f3a" stroke="#3d2715" strokeWidth="0.8"/>
      </g>
      {/* 中心问号 */}
      <text x="50" y="58" fontSize="22" fontFamily="serif" fontWeight="bold" textAnchor="middle" fill="#fff5dc">?</text>
      {/* 连接线 */}
      <g stroke="#5b3a1f" strokeWidth="1" strokeDasharray="2 2" opacity="0.5">
        <line x1="50" y1="30" x2="50" y2="42"/>
        <line x1="70" y1="50" x2="58" y2="50"/>
        <line x1="50" y1="70" x2="50" y2="58"/>
        <line x1="30" y1="50" x2="42" y2="50"/>
      </g>
    </g>
  );
}

/* 8. 任务导航仪 — 罗盘 */
function CompassIcon() {
  return (
    <g>
      {/* 外圈 */}
      <circle cx="50" cy="50" r="32" fill="#5b3a1f" stroke="#3d2715" strokeWidth="2"/>
      <circle cx="50" cy="50" r="28" fill="#f4e9d2" stroke="#3d2715" strokeWidth="1"/>
      {/* 方位 */}
      <text x="50" y="28" fontSize="8" fontFamily="serif" fontWeight="bold" textAnchor="middle" fill="#3d2715">N</text>
      <text x="50" y="78" fontSize="8" fontFamily="serif" fontWeight="bold" textAnchor="middle" fill="#3d2715">S</text>
      <text x="74" y="54" fontSize="8" fontFamily="serif" fontWeight="bold" textAnchor="middle" fill="#3d2715">E</text>
      <text x="26" y="54" fontSize="8" fontFamily="serif" fontWeight="bold" textAnchor="middle" fill="#3d2715">W</text>
      {/* 刻度 */}
      {Array.from({length:24}).map((_,i)=>{
        const a = (i*15) * Math.PI/180;
        const r1 = i%6===0 ? 24 : 26;
        const r2 = 28;
        return <line key={i}
          x1={50 + Math.cos(a)*r1} y1={50 + Math.sin(a)*r1}
          x2={50 + Math.cos(a)*r2} y2={50 + Math.sin(a)*r2}
          stroke="#5b3a1f" strokeWidth={i%6===0?1.4:0.6}/>;
      })}
      {/* 指针 */}
      <g transform="rotate(35 50 50)">
        <polygon points="50,28 46,50 54,50" fill="#a85a32" stroke="#3d2715" strokeWidth="0.8"/>
        <polygon points="50,72 46,50 54,50" fill="#fff5dc" stroke="#3d2715" strokeWidth="0.8"/>
      </g>
      {/* 中心轴 */}
      <circle cx="50" cy="50" r="3" fill="#3d2715"/>
      <circle cx="50" cy="50" r="1.2" fill="#ffd98a"/>
    </g>
  );
}

/* ---------- AI 头像 ---------- */

function AIAvatar({ role, size = 48, thinking = false }) {
  const source = `assets/exhibition/roles/${role.id}.jpg`;
  const [failedSource, setFailedSource] = useState('');
  const [loadedSource, setLoadedSource] = useState('');
  const failed = failedSource === source;
  const loaded = loadedSource === source && !failed;
  return (
    <div className={`ai-avatar ${thinking ? 'is-thinking' : ''}`} style={{ width: size, height: size }} title={role.roleName || role.fullName}>
      {!loaded && <div className="ai-avatar-fallback"><RoleCharacterFallback role={role} size={size} /></div>}
      {!failed && <img src={source} alt={`${role.roleName || role.fullName || 'AI 助手'}头像`} className={loaded ? 'is-loaded' : ''}
        width={size} height={size} decoding="async" onLoad={() => setLoadedSource(source)} onError={() => setFailedSource(source)} />}
      {thinking && <span className="ai-avatar-status" role="status" aria-label="正在思考" />}
    </div>
  );
}

/* ---------- 角色徽章 ---------- */

function RoleBadge({ role, className = '' }) {
  return (
    <div className={`inline-flex items-center gap-2 max-w-full ${className}`}>
      <AIAvatar role={role} size={32} />
      <div className="leading-tight min-w-0">
        <div className="text-xs text-sand-700 truncate">{role.className} · {role.teamName}</div>
        <div className="font-display text-sand-900 text-sm truncate">{role.roleName}</div>
      </div>
    </div>
  );
}

/* ---------- 沙瓶进度 ---------- */

function SandJar({ student }) {
  const completed = STAGES.filter(s => student.stages?.[s.key]?.status === 'completed');
  const inProgress = STAGES.find(s => student.stages?.[s.key]?.status === 'in-progress');

  // 每层高度: 完成的层各占 100/5 = 20%; 进行中的层占 10%
  const totalFill = completed.length * 20 + (inProgress ? 10 : 0);
  const layerColors = { learn: '#756142', ask: '#806747', think: '#396762', debate: '#485d69', act: '#66513d' };

  let cursor = 0;
  return (
    <div className="sand-jar exhibition-sand-jar" role="progressbar" aria-label="五阶段学习积累"
      aria-valuemin={0} aria-valuemax={100} aria-valuenow={totalFill}
      aria-valuetext={`已完成 ${completed.length} 个阶段，共 ${STAGES.length} 个阶段${inProgress ? `，${inProgress.name}进行中` : ''}`}>
      <div className="sand-jar-grid" aria-hidden="true" />
      {/* 已完成层 - 从底向上堆叠 */}
      {completed.map((s, i) => {
        const h = 20;
        const bottom = cursor;
        cursor += h;
        return (
          <div
            key={s.key}
            className="sand-jar-layer flex items-center justify-center"
            style={{
              bottom: `${bottom}%`, height: `${h}%`,
              background: `linear-gradient(180deg, ${layerColors[s.key] || '#756142'}dd 0%, ${layerColors[s.key] || '#756142'} 100%)`,
              borderTop: '1px solid rgba(246,240,229,0.25)',
            }}
          >
            <span className="text-white/85 font-display text-lg drop-shadow-sm">{s.title}</span>
          </div>
        );
      })}
      {/* 进行中层 */}
      {inProgress && (
        <div
          className="sand-jar-layer flex items-center justify-center"
          style={{
            bottom: `${cursor}%`, height: `10%`,
            background: `linear-gradient(180deg, ${layerColors[inProgress.key] || '#756142'}99, ${layerColors[inProgress.key] || '#756142'})`,
            borderTop: '1px solid rgba(255,255,255,0.35)',
            opacity: 0.65,
          }}
        >
          <span className="text-white/95 text-sm font-medium">+ {inProgress.title} 进行中</span>
        </div>
      )}
      {/* 沙瓶口 */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-2 rounded-b-full bg-sand-700/30" />
    </div>
  );
}

/* ---------- 顶部条 ---------- */

function TopBar({ left, right, center }) {
  return (
    <header className="exhibition-topbar">
      <div className="content-max exhibition-topbar-inner px-4 sm:px-8 py-3 sm:py-4 flex items-center justify-between gap-2 sm:gap-4 relative z-10 flex-wrap">
        <div className="exhibition-topbar-left flex items-center gap-3 min-w-0">{left}</div>
        <div className="exhibition-topbar-center flex-1 hidden lg:flex justify-center min-w-0">{center}</div>
        <nav aria-label="页面导航" className="exhibition-topbar-actions flex items-center gap-2 sm:gap-3 flex-wrap justify-end">{right}</nav>
      </div>
    </header>
  );
}

/* ---------- 项目 Logo ---------- */

function ProjectLogo({ small = false }) {
  return (
    <div className={`project-logo flex items-center gap-3 ${small ? 'project-logo-small' : ''}`}>
      <div className="project-logo-symbol" aria-hidden="true">
        <svg viewBox="0 0 56 56" fill="none">
          <circle cx="28" cy="28" r="24" stroke="currentColor" strokeOpacity=".36" strokeWidth=".8" />
          <ellipse cx="28" cy="28" rx="25" ry="9" transform="rotate(-38 28 28)" stroke="currentColor" strokeOpacity=".6" strokeWidth=".8" />
          <path d="M9 36c8-1 14-16 20-14 6 2 6 13 18 14M12 39c11-1 15-10 22-9 5 .7 7 6 12 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          <path d="m29 22 5 8-7-4" fill="currentColor" fillOpacity=".14" />
          <circle cx="39" cy="13" r="2" fill="currentColor" />
          <circle cx="13" cy="40" r="1.4" fill="#8bcbc2" />
        </svg>
      </div>
      <div className="project-logo-type leading-tight">
        <div className="project-logo-name font-display">{PROJECT.name}</div>
        <div className="project-logo-subtitle">{small ? '沙海未来学习博物馆' : PROJECT.subtitle}</div>
      </div>
    </div>
  );
}

/* ---------- 用户菜单 ---------- */

function UserMenu({ label, sub, onLogout, color = 'sand' }) {
  return (
    <div className="flex items-center gap-2 sm:gap-3">
      <div className="text-right leading-tight hidden sm:block">
        <div className="text-sm font-medium text-sand-900 truncate max-w-[120px]">{label}</div>
        <div className="text-xs text-sand-700 truncate max-w-[140px]">{sub}</div>
      </div>
      <button type="button" className="sand-button sand-button-ghost text-xs sm:text-sm px-3 py-1.5 sm:py-2" onClick={onLogout}><ExhibitionIcon name="logout" size={15} />退出</button>
    </div>
  );
}

/* ---------- 通用 Chip ---------- */

function Chip({ children, variant }) {
  return <span className={`chip ${variant || ''}`}>{children}</span>;
}

/* ---------- 展览界面线性图标与全屏入口 ---------- */

function ExhibitionIcon({ name = 'spark', size = 20, className = '', label, ...props }) {
  const paths = {
    arrow: <><path d="M4 12h15M13 6l6 6-6 6" /></>,
    'arrow-left': <><path d="M20 12H5m6-6-6 6 6 6" /></>,
    fullscreen: <><path d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5" /></>,
    'exit-fullscreen': <><path d="M3 8h5V3m13 5h-5V3M8 21v-5H3m13 5v-5h5" /></>,
    spark: <><path d="m12 3 2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5Z" /><path d="M19 2v4m-2-2h4" /></>,
    grid: <><rect x="3" y="3" width="7" height="7" rx="1.4" /><rect x="14" y="3" width="7" height="7" rx="1.4" /><rect x="3" y="14" width="7" height="7" rx="1.4" /><rect x="14" y="14" width="7" height="7" rx="1.4" /></>,
    search: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" /></>,
    close: <path d="m6 6 12 12M6 18 18 6" />,
    chevron: <path d="m9 5 7 7-7 7" />,
    'chevron-down': <path d="m5 9 7 7 7-7" />,
    upload: <><path d="M12 16V3m-5 5 5-5 5 5M4 15v5a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-5" /></>,
    play: <path d="m8 5 11 7-11 7Z" />,
    book: <><path d="M12 5v15M3 4c4-1 7-1 9 1 2-2 5-2 9-1v15c-4-1-7-1-9 1-2-2-5-2-9-1Z" /></>,
    compass: <><circle cx="12" cy="12" r="9" /><path d="m16 8-2.5 5.5L8 16l2.5-5.5Z" /></>,
    check: <path d="m5 12 4 4L19 6" />,
    chat: <><path d="M20 15a3 3 0 0 1-3 3H9l-5 3V6a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3Z" /><path d="M8 8h8M8 12h5" /></>,
    user: <><circle cx="12" cy="8" r="4" /><path d="M4 21v-2a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v2" /></>,
    logout: <><path d="M9 4H4v16h5m1-8h11m-4-4 4 4-4 4" /></>,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
    strokeLinecap="round" strokeLinejoin="round" className={`exhibition-icon ${className}`}
    role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true} focusable="false" {...props}>
    {paths[name] || paths.spark}
  </svg>;
}

function ExhibitionFullscreen({ className = '', compact = false }) {
  const [active, setActive] = useState(!!(document.fullscreenElement || document.webkitFullscreenElement));
  const [message, setMessage] = useState('');
  useEffect(() => {
    const update = () => setActive(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener('fullscreenchange', update);
    document.addEventListener('webkitfullscreenchange', update);
    return () => {
      document.removeEventListener('fullscreenchange', update);
      document.removeEventListener('webkitfullscreenchange', update);
    };
  }, []);
  async function toggleFullscreen() {
    setMessage('');
    try {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        const leave = document.exitFullscreen || document.webkitExitFullscreen;
        if (leave) await leave.call(document);
      } else {
        const enter = document.documentElement.requestFullscreen || document.documentElement.webkitRequestFullscreen;
        if (!enter) {
          setMessage('当前浏览器不支持网页全屏，可通过浏览器“显示”菜单进入全屏。');
          return;
        }
        await enter.call(document.documentElement);
      }
    } catch (error) {
      setMessage('当前窗口暂时无法进入全屏，请在 Chrome 或 Safari 独立窗口中打开后重试。');
    }
  }
  const label = active ? '退出全屏' : '全屏观展';
  return <span className="exhibition-fullscreen-control">
    <button type="button" className={`sand-button sand-button-ghost exhibition-fullscreen ${className}`} onClick={toggleFullscreen}
      aria-label={label} aria-pressed={active} title={label}>
      <ExhibitionIcon name={active ? 'exit-fullscreen' : 'fullscreen'} size={17} />{!compact && <span>{label}</span>}
    </button>
    {message && <span className="exhibition-fullscreen-message" role="status">{message}</span>}
  </span>;
}

/* ---------- 阶段完成庆祝层 ---------- */

function CelebrateOverlay({ stage, role, allDone, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, allDone ? 4200 : 3000);
    return () => clearTimeout(t);
  }, [onClose, allDone]);

  const colors = ['#ffcd6a','#a3661f','#d6a86a','#6ea674','#a85a32','#fff5dc'];
  const particles = Array.from({ length: 60 }).map((_, i) => ({
    left: Math.random() * 100,
    size: 4 + Math.random() * 8,
    duration: 1.6 + Math.random() * 1.8,
    delay: Math.random() * 0.6,
    color: colors[i % colors.length],
  }));

  return (
    <div className="celebrate-overlay" onClick={onClose}>
      <div className="confetti-sand">
        {particles.map((p, i) => (
          <span key={i} style={{
            left: `${p.left}%`,
            width: `${p.size}px`, height: `${p.size}px`,
            background: p.color,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            opacity: 0.85,
          }} />
        ))}
      </div>
      <div className="celebrate-emoji">{allDone ? '🏁' : stage.icon}</div>
      <div className="celebrate-title">
        {allDone ? `${role.teamName} 全程完成！` : `恭喜！「${stage.name}」`}
      </div>
      <div className="celebrate-sub">
        {allDone
          ? '一沙一世界 · 五阶段终章'
          : `你已完成 ${stage.title}，接下来——继续走向下一关。`}
      </div>
      <div className="celebrate-tip">{allDone ? '点击查看你的成长沙瓶' : '点击任意位置继续'}</div>
    </div>
  );
}

/* ---------- 提示 toast ---------- */

function useToast() {
  const [list, setList] = useState([]);
  function toast(text, kind = 'info') {
    const id = Date.now() + Math.random();
    setList(l => [...l, { id, text, kind }]);
    setTimeout(() => setList(l => l.filter(t => t.id !== id)), 2600);
  }
  const ToastView = (
    <div className="fixed right-4 bottom-4 z-[200] flex flex-col gap-2 items-end pointer-events-none">
      {list.map(t => (
        <div key={t.id}
             className={`fade-up paper-card px-4 py-2 text-sm shadow-sand-soft pointer-events-auto
                         ${t.kind === 'success' ? 'border-oasis-500/40' : t.kind === 'error' ? 'border-terra-500/50' : ''}`}>
          {t.kind === 'success' && <span className="mr-1">✅</span>}
          {t.kind === 'error' && <span className="mr-1">⚠️</span>}
          {t.text}
        </div>
      ))}
    </div>
  );
  return { toast, ToastView };
}
